import React, { useState } from "react";
import { apiPostNoAuth } from "../services/api";
import { Link } from "react-router-dom";
import Footer from "./Footer";

export default function ForgotPassword() {
  const [step, setStep] = useState(1);
  const [identifier, setIdentifier] = useState("");
  const [maskedEmail, setMaskedEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const handleRequestOtp = async () => {
    if (!identifier) {
      setError("Please enter enrollment or roll number.");
      return;
    }
    setLoading(true);
    setError("");
    setSuccess("");
    try {
      const res = await apiPostNoAuth("student_forgot_password", {
        action: "requestOtp",
        identifier
      });
      if (res.success) {
        setMaskedEmail(res.maskedEmail);
        setStep(2);
        setSuccess("OTP sent successfully.");
      } else {
        setError(res.error || "Something went wrong.");
      }
    } catch (e) {
      setError("Failed to request OTP.");
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setResending(true);
    setError("");
    setSuccess("");
    try {
      const res = await apiPostNoAuth("student_forgot_password", {
        action: "requestOtp",
        identifier
      });
      if (res.success) {
        setSuccess("OTP resent successfully.");
      } else {
        setError(res.error || "Could not resend OTP.");
      }
    } catch (e) {
      setError("Failed to resend OTP.");
    } finally {
      setResending(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otp) {
      setError("Please enter the OTP.");
      return;
    }
    setLoading(true);
    setError("");
    setSuccess("");
    try {
      const res = await apiPostNoAuth("student_forgot_password", {
        action: "verifyOtp",
        identifier,
        otp
      });
      if (res.success) {
        setStep(3);
        setSuccess("OTP verified successfully.");
      } else {
        setError(res.error || "Invalid OTP.");
      }
    } catch (e) {
      setError("Failed to verify OTP.");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword) {
      setError("Please enter a new password.");
      return;
    }
    setLoading(true);
    setError("");
    setSuccess("");
    try {
      const res = await apiPostNoAuth("student_forgot_password", {
        action: "resetPassword",
        identifier,
        newPassword
      });
      if (res.success) {
        setSuccess("Password reset successfully. You may now log in.");
        setStep(4);
      } else {
        setError(res.error || "Could not reset password.");
      }
    } catch (e) {
      setError("Failed to reset password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-100 px-4">
      <div className="w-full max-w-md bg-white p-6 rounded-lg shadow-lg">
        <h2 className="text-2xl font-semibold text-center mb-4">Forgot Password</h2>

        {error && (
          <div className="bg-red-100 text-red-700 px-3 py-2 rounded mb-3 text-sm">
            {error}
          </div>
        )}
        {success && (
          <div className="bg-green-100 text-green-700 px-3 py-2 rounded mb-3 text-sm">
            {success}
          </div>
        )}

        {step === 1 && (
          <>
            <label className="block mb-2 text-sm font-medium">
              Enrollment or Roll Number
            </label>
            <input
              type="text"
              className="w-full border border-gray-300 rounded px-3 py-2 mb-4 focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
            />
            <button
              onClick={handleRequestOtp}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 rounded transition duration-150"
              disabled={loading}
            >
              {loading ? "Sending OTP..." : "Send OTP"}
            </button>
          </>
        )}

        {step === 2 && (
          <>
            <p className="mb-4 text-sm">
              An OTP has been sent to <strong>{maskedEmail}</strong>.
            </p>
            <label className="block mb-2 text-sm font-medium">Enter OTP</label>
            <input
              type="text"
              className="w-full border border-gray-300 rounded px-3 py-2 mb-4 focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
            />
            <button
              onClick={handleVerifyOtp}
              className="w-full bg-green-600 hover:bg-green-700 text-white font-medium py-2 rounded transition duration-150 mb-2"
              disabled={loading}
            >
              {loading ? "Verifying..." : "Verify OTP"}
            </button>
            <button
              onClick={handleResendOtp}
              className="w-full bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium py-2 rounded transition duration-150"
              disabled={resending}
            >
              {resending ? "Resending..." : "Resend OTP"}
            </button>
          </>
        )}

        {step === 3 && (
          <>
            <label className="block mb-2 text-sm font-medium">New Password</label>
            <input
              type="password"
              className="w-full border border-gray-300 rounded px-3 py-2 mb-4 focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
            <button
              onClick={handleResetPassword}
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-medium py-2 rounded transition duration-150"
              disabled={loading}
            >
              {loading ? "Resetting..." : "Reset Password"}
            </button>
          </>
        )}

        {step === 4 && (
          <div className="text-center">
            <p className="mb-4">Your password has been reset successfully.</p>
            <Link
              to="/"
              className="inline-block bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded transition duration-150"
            >
              Go to Login
            </Link>
          </div>
        )}

        {/* Login Link */}
                <div className="text-center text-sm text-gray-600 mt-5">
                  Already have an account?{" "}          
                  <Link
                              to="/"
                              className="text-blue-600 font-medium hover:underline"
                            >
                              Login
                            </Link>
                </div>
        {/* Footer */}
      <Footer />
      </div>
      
    </div>
    
  );
}
